import React, { useState, useEffect } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import {
  GripVertical,
  Calendar,
  User,
  Flag,
  ChevronDown,
  ChevronRight,
  Plus,
  X,
  TrendingUp,
  Target,
  Search,
  Filter,
  Paperclip,
} from "lucide-react";
import { Task, ColumnType } from "../../types/types";
import StoryModal from "../StoryModal/StoryModal";
import { useNotifications } from "../NotificationSystem/NotificationSystem";
import { realAPI } from "../../services/realAPI";

interface Sprint {
  id: string;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  status: "planning" | "active" | "completed";
}

interface BacklogViewProps {
  sprints: Sprint[];
  selectedTeam: any;
  backlogItems?: Task[];
  onUpdateBacklogItems?: (items: Task[]) => void;
  onCreateBacklogItem?: (
    item: Task & {
      attachments?: Array<{
        originalFilename: string;
        url: string;
        thumbnailUrl?: string;
      }>;
    }
  ) => Promise<Task> | Task;
  onUpdateTask?: (task: Task) => void;
  onReady?: () => void; // called when initial backlog data is ready
}

const BacklogView: React.FC<BacklogViewProps> = ({
  sprints,
  selectedTeam,
  backlogItems: externalBacklogItems,
  onUpdateBacklogItems,
  onCreateBacklogItem,
  onUpdateTask,
  onReady,
}) => {
  const [backlogItems, setBacklogItems] = useState<Task[]>(
    externalBacklogItems || []
  );
  const [collapsedSprints, setCollapsedSprints] = useState<Set<string>>(
    new Set()
  );
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<
    "all" | "story" | "bug" | "task"
  >("all");
  const [filterAssignee, setFilterAssignee] = useState<"all" | string>("all");
  const [filterPriority, setFilterPriority] = useState<
    "all" | "low" | "medium" | "high" | "urgent"
  >("all");
  const { success, error } = useNotifications();
  const [newStory, setNewStory] = useState({
    title: "",
    description: "",
    priority: "medium" as "high" | "medium" | "low",
    storyPoints: "",
    type: "story" as "story" | "bug" | "task",
    assignTo: "backlog",
  });
  const [isCreatingStory, setIsCreatingStory] = useState(false);
  const [storyAttachments, setStoryAttachments] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(
    new Map()
  );
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Fetch all tasks (backlog and sprints) and flatten into one array for UI
  useEffect(() => {
    const fetchAllTasks = async () => {
      try {
        const { realAPI } = await import("../../services/realAPI");
        const teamId = selectedTeam?.id || 1;
        const allTasks = await realAPI.getAllTasksWithSprints(teamId);
        setBacklogItems(allTasks);
        setIsInitialLoading(false);
        // Notify parent that backlog data is ready
        if (onReady) onReady();
      } catch (err) {
        error("Failed to fetch all tasks", (err as Error).message);
        setIsInitialLoading(false);
        // Even on error, allow parent to stop showing the spinner
        if (onReady) onReady();
      }
    };
    fetchAllTasks();
    // We only want to refetch when selectedTeam changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "text-red-600";
      case "medium":
        return "text-yellow-600";
      case "low":
        return "text-green-600";
      default:
        return "text-gray-600";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "story":
        return "📖";
      case "bug":
        return "🐛";
      case "task":
        return "✅";
      default:
        return "📋";
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId) {
      return; // Same container, no change needed for now
    }

    // Find the dragged item
    const draggedItem = backlogItems.find(
      (item) => String(item.id) === draggableId
    );
    if (!draggedItem) return;

    // Prepare updated task
    const updatedTask = {
      ...draggedItem,
      status: destination.droppableId,
      // If moving to a sprint, add kanban column; if moving to backlog, remove it
      taskColumn:
        destination.droppableId === "backlog"
          ? undefined
          : ("todo" as ColumnType),
    };

    try {
      const { realAPI } = await import("../../services/realAPI");
      // Call backend to update task status/column
      const updatedFromBackend = await realAPI.updateTaskStatusAndColumn(
        updatedTask.id,
        updatedTask.status,
        updatedTask.taskColumn || null
      );

      // Force status and taskColumn in local state in case backend does not echo them correctly
      const forcedUpdatedTask = {
        ...updatedFromBackend,
        status: updatedTask.status,
        taskColumn: updatedTask.taskColumn,
      };

      const updatedItems = backlogItems.map((item) =>
        String(item.id) === draggableId ? forcedUpdatedTask : item
      );
      setBacklogItems(updatedItems);
      if (onUpdateBacklogItems) {
        onUpdateBacklogItems(updatedItems);
      }
      success("Task updated", `Task moved to ${updatedTask.status}`);
    } catch (err) {
      error("Failed to update task", (err as Error).message);
    }
  };

  const getItemsForContainer = (containerId: string): Task[] => {
    return backlogItems.filter(
      (item) => String(item.status) === String(containerId)
    );
  };

  // Get unique assignees for filter dropdown - using assigneeUserId for now
  const uniqueAssignees = Array.from(
    new Set(
      backlogItems
        .map((item) => item.assigneeUserId || item.assignee)
        .filter(Boolean)
    )
  );

  // Filter backlog items based on search and filters
  const filteredBacklogItems = backlogItems.filter((item) => {
    const matchesSearch =
      !searchTerm ||
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === "all" || item.type === filterType;
    const matchesAssignee =
      filterAssignee === "all" ||
      item.assigneeUserId === filterAssignee ||
      item.assignee === filterAssignee;
    const matchesPriority =
      filterPriority === "all" || item.priority === filterPriority;

    return matchesSearch && matchesType && matchesAssignee && matchesPriority;
  });

  const futureSprints = sprints
    .filter(
      (sprint) => sprint.status === "planning" || sprint.status === "active"
    )
    .sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );

  const toggleSprintCollapse = (sprintId: string) => {
    const newCollapsed = new Set(collapsedSprints);
    if (newCollapsed.has(sprintId)) {
      newCollapsed.delete(sprintId);
    } else {
      newCollapsed.add(sprintId);
    }
    setCollapsedSprints(newCollapsed);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // File handling functions for story creation
  const isImageFile = (file: File) => {
    return file.type.startsWith("image/");
  };

  const createFilePreview = (file: File): string => {
    if (isImageFile(file)) {
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`;

      // Check if we already have a preview URL for this file
      const existingUrl = previewUrls.get(fileKey);
      if (existingUrl) {
        return existingUrl;
      }

      // Create new URL and store it
      const newUrl = URL.createObjectURL(file);
      setPreviewUrls((prev) => new Map(prev).set(fileKey, newUrl));
      return newUrl;
    }
    return "";
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);

      // Validate file types and sizes
      const validFiles = fileArray.filter((file) => {
        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
          "application/pdf",
          "text/plain",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];

        if (!allowedTypes.includes(file.type)) {
          error(
            "Invalid File Type",
            `${file.name} is not a supported file type.`
          );
          return false;
        }

        if (file.size > 10 * 1024 * 1024) {
          // 10MB limit
          error("File Too Large", `${file.name} is larger than 10MB.`);
          return false;
        }

        return true;
      });

      setStoryAttachments((prev) => [...prev, ...validFiles]);

      // Create preview URLs for new image files
      validFiles.forEach((file) => {
        if (isImageFile(file)) {
          createFilePreview(file);
        }
      });
    }

    // Clear the input
    event.target.value = "";
  };

  const removeAttachment = (index: number) => {
    const fileToRemove = storyAttachments[index];

    // Clean up preview URL to prevent memory leaks
    if (isImageFile(fileToRemove)) {
      const fileKey = `${fileToRemove.name}-${fileToRemove.size}-${fileToRemove.lastModified}`;
      const previewUrl = previewUrls.get(fileKey);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrls((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileKey);
          return newMap;
        });
      }
    }

    setStoryAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadAttachmentsBeforeCreate = async (): Promise<
    Array<{
      originalFilename: string;
      url: string;
      thumbnailUrl?: string;
    }>
  > => {
    if (storyAttachments.length === 0) {
      return [];
    }

    setIsUploadingFiles(true);
    try {
      const uploadResponse = await realAPI.uploadAttachments(storyAttachments);

      if (uploadResponse && uploadResponse.success && uploadResponse.data) {
        // Transform to include thumbnailUrl
        return uploadResponse.data.map((file: any) => ({
          originalFilename: file.originalFilename || file.filename,
          url: file.url || file.filePath,
          thumbnailUrl: file.thumbnailUrl,
        }));
      }

      return [];
    } catch (uploadError) {
      console.error("❌ Error uploading attachments:", uploadError);
      error("Upload Failed", "Failed to upload attachments. Please try again.");
      throw uploadError;
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleCreateStory = async () => {
    if (!newStory.title.trim()) {
      error("Title Required", "Please enter a title for the story.");
      return;
    }

    setIsCreatingStory(true);

    try {
      // First upload attachments if any
      let attachments: Array<{
        originalFilename: string;
        url: string;
      }> = [];

      if (storyAttachments.length > 0) {
        try {
          attachments = await uploadAttachmentsBeforeCreate();
        } catch (uploadError) {
          // Upload failed, stop story creation
          setIsCreatingStory(false);
          return;
        }
      }

      const tempStory = {
        id: `item-${Date.now()}`,
        title: newStory.title,
        description: newStory.description,
        priority: newStory.priority,
        storyPoints: newStory.storyPoints
          ? parseInt(newStory.storyPoints)
          : undefined,
        type: newStory.type,
        status: newStory.assignTo,
        assignee: undefined,
        taskColumn: "todo",
        // Add attachments to the story data for backend creation
        attachments: attachments.length > 0 ? attachments : undefined,
        // Keep backward compatibility with attachmentUrls
        attachmentUrls: attachments.map((att) => att.url),
      } as Task & {
        attachments?: Array<{
          originalFilename: string;
          url: string;
          thumbnailUrl?: string;
        }>;
      };

      // Call parent to create in backend and get real ID synchronously
      if (onCreateBacklogItem) {
        // Create the task first
        const createdTask = await Promise.resolve(
          onCreateBacklogItem(tempStory)
        );

        if (createdTask && createdTask.id) {
          // Update the created task with attachment URLs
          const taskWithAttachments = {
            ...createdTask,
            attachmentUrls: attachments.map((att) => att.url),
          };

          // Add the created task with attachments to the list
          setBacklogItems((prev) => [...prev, taskWithAttachments]);

          if (onUpdateBacklogItems) {
            onUpdateBacklogItems([...backlogItems, taskWithAttachments]);
          }

          success(
            "Story Created",
            `"${createdTask.title}" has been added to the ${
              newStory.assignTo === "backlog" ? "backlog" : "sprint"
            }.${
              attachments.length > 0
                ? ` ${attachments.length} file(s) attached.`
                : ""
            }`
          );
        }
      } else {
        // Fallback: add temp story if no parent handler
        setBacklogItems((prev) => [...prev, tempStory]);

        success(
          "Story Created",
          `"${tempStory.title}" has been added to the ${
            newStory.assignTo === "backlog" ? "backlog" : "sprint"
          }.${
            attachments.length > 0
              ? ` ${attachments.length} file(s) attached.`
              : ""
          }`
        );
      }

      // Reset form
      // Clean up preview URLs
      storyAttachments.forEach((file) => {
        if (isImageFile(file)) {
          const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
          const previewUrl = previewUrls.get(fileKey);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
        }
      });

      setNewStory({
        title: "",
        description: "",
        priority: "medium",
        storyPoints: "",
        type: "story",
        assignTo: "backlog",
      });
      setStoryAttachments([]);
      setPreviewUrls(new Map());
      setShowCreateStory(false);
    } catch (err) {
      const message = (err as Error)?.message || String(err);
      if (/insufficient balance for stories/i.test(message)) {
        error(
          "Insufficient Stories Balance",
          "No remaining story credits. Please top up your STORIES quota in Quotas & Balance."
        );
      } else {
        error("Failed to create story", message);
      }
    } finally {
      setIsCreatingStory(false);
    }
  };

  const handleCancelCreate = () => {
    if (isCreatingStory) return; // Prevent canceling while creating

    // Clean up preview URLs
    storyAttachments.forEach((file) => {
      if (isImageFile(file)) {
        const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
        const previewUrl = previewUrls.get(fileKey);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
      }
    });

    setNewStory({
      title: "",
      description: "",
      priority: "medium",
      storyPoints: "",
      type: "story",
      assignTo: "backlog",
    });
    setStoryAttachments([]);
    setPreviewUrls(new Map());
    setShowCreateStory(false);
  };

  const handleBacklogItemClick = (task: Task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setSelectedTask(null);
    setIsModalOpen(false);
  };

  const getSprintSummary = (sprintId: string) => {
    const sprintItems = getItemsForContainer(sprintId);
    const totalStoryPoints = sprintItems.reduce(
      (sum, item) => sum + (item.storyPoints || 0),
      0
    );
    const totalTasks = sprintItems.length;
    const assignedTasks = sprintItems.filter(
      (item) => item.assigneeUserId || item.assignee
    ).length;

    return {
      totalTasks,
      totalStoryPoints,
      assignedTasks,
      unassignedTasks: totalTasks - assignedTasks,
    };
  };

  const renderBacklogItem = (item: Task, index: number) => (
    <Draggable key={item.id} draggableId={String(item.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          onClick={() => handleBacklogItemClick(item)}
          className={`flex items-center space-x-3 p-3 bg-white border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer ${
            snapshot.isDragging ? "shadow-lg border-blue-300" : ""
          }`}
        >
          <div
            {...provided.dragHandleProps}
            className="text-gray-400 hover:text-gray-600"
          >
            <GripVertical className="w-4 h-4" />
          </div>

          <span className="text-lg">{getTypeIcon(item.type || "story")}</span>

          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <h4 className="text-sm font-medium text-gray-900 truncate">
                {item.title}
              </h4>
              <Flag
                className={`w-3 h-3 ${getPriorityColor(
                  item.priority || "medium"
                )}`}
              />
            </div>
            <p className="text-xs text-gray-500 truncate">{item.description}</p>
          </div>

          <div className="flex items-center space-x-3 text-xs text-gray-500">
            {item.storyPoints && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                {item.storyPoints}
              </span>
            )}
            {((item.attachments && item.attachments.length > 0) ||
              (item.attachmentUrls && item.attachmentUrls.length > 0)) && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                <Paperclip className="w-3 h-3" />
                <span>
                  {(item.attachments?.length || 0) +
                    (item.attachmentUrls?.length || 0)}
                </span>
              </div>
            )}
            {item.assignee && (
              <div className="flex items-center space-x-1">
                <User className="w-3 h-3" />
                <span>{item.assignee}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header with Create Story Button and Search/Filters */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Backlog Planning
              </h1>
              <p className="text-gray-600">
                Manage your product backlog and plan future sprints
              </p>
            </div>
            <div className="flex items-center gap-3">
              {isInitialLoading && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
              )}
              <button
                onClick={() => setShowCreateStory(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Story
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-4 rounded-lg">
            {/* Search */}
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search stories, bugs, and tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Type Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) =>
                  setFilterType(e.target.value as typeof filterType)
                }
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="story">Stories</option>
                <option value="bug">Bugs</option>
                <option value="task">Tasks</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <select
                value={filterPriority}
                onChange={(e) =>
                  setFilterPriority(e.target.value as typeof filterPriority)
                }
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Assignee Filter */}
            <div>
              <select
                value={filterAssignee}
                onChange={(e) => setFilterAssignee(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Assignees</option>
                <option value="">Unassigned</option>
                {uniqueAssignees.map((assignee) => (
                  <option key={assignee} value={assignee}>
                    {assignee}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {(searchTerm ||
              filterType !== "all" ||
              filterPriority !== "all" ||
              filterAssignee !== "all") && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setFilterType("all");
                  setFilterPriority("all");
                  setFilterAssignee("all");
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Create Story Modal */}
        {showCreateStory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-8 max-h-[calc(100vh-2rem)] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Create New Story
                </h2>
                <button
                  onClick={handleCancelCreate}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={newStory.title}
                    onChange={(e) =>
                      setNewStory((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter story title..."
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newStory.description}
                    onChange={(e) =>
                      setNewStory((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter story description..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={newStory.type}
                      onChange={(e) =>
                        setNewStory((prev) => ({
                          ...prev,
                          type: e.target.value as "story" | "bug" | "task",
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="story">📖 Story</option>
                      <option value="bug">🐛 Bug</option>
                      <option value="task">✅ Task</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={newStory.priority}
                      onChange={(e) =>
                        setNewStory((prev) => ({
                          ...prev,
                          priority: e.target.value as "high" | "medium" | "low",
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="high">🔴 High</option>
                      <option value="medium">🟡 Medium</option>
                      <option value="low">🟢 Low</option>
                    </select>
                  </div>

                  {/* Story Points */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Story Points
                    </label>
                    <input
                      type="number"
                      value={newStory.storyPoints}
                      onChange={(e) =>
                        setNewStory((prev) => ({
                          ...prev,
                          storyPoints: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Points"
                      min="1"
                      max="100"
                    />
                  </div>
                </div>

                {/* Assign To */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assign To
                  </label>
                  <select
                    value={newStory.assignTo}
                    onChange={(e) =>
                      setNewStory((prev) => ({
                        ...prev,
                        assignTo: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="backlog">📋 Backlog (Default)</option>
                    {futureSprints.map((sprint) => (
                      <option key={sprint.id} value={sprint.id}>
                        🏃 {sprint.name} ({formatDate(sprint.startDate)} -{" "}
                        {formatDate(sprint.endDate)})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Attachments Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Attachments (Optional)
                  </label>

                  {/* File Upload Button */}
                  <div className="mb-3">
                    <label className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-lg border border-purple-300 cursor-pointer hover:bg-purple-200 transition-colors">
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                        />
                      </svg>
                      Choose Files
                      <input
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        accept="image/*,.pdf,.doc,.docx,.txt"
                        className="hidden"
                        disabled={isCreatingStory || isUploadingFiles}
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Images, PDFs, and documents up to 10MB each
                    </p>
                  </div>

                  {/* Selected Files List */}
                  {storyAttachments.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-3">
                      {storyAttachments.map((file, index) => (
                        <div
                          key={index}
                          className="relative bg-gray-50 p-4 rounded border flex flex-col items-center"
                        >
                          {/* Remove button - positioned at top right */}
                          <button
                            onClick={() => removeAttachment(index)}
                            disabled={isCreatingStory || isUploadingFiles}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700 disabled:text-gray-400 p-1 bg-white rounded-full shadow-sm"
                          >
                            <X className="w-4 h-4" />
                          </button>

                          {/* File thumbnail or icon */}
                          {isImageFile(file) ? (
                            <img
                              src={createFilePreview(file)}
                              alt={file.name}
                              className="w-48 h-48 rounded object-cover border mb-3"
                            />
                          ) : (
                            <div className="w-48 h-48 bg-gray-200 rounded flex items-center justify-center mb-3">
                              <svg
                                className="w-20 h-20 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                />
                              </svg>
                            </div>
                          )}

                          {/* File info */}
                          <div className="text-center w-full px-2">
                            <span
                              className="text-sm text-gray-700 block truncate mb-1"
                              title={file.name}
                            >
                              {file.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {(file.size / 1024).toFixed(1)}KB
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {isUploadingFiles && (
                    <div className="flex items-center space-x-2 text-blue-600 mb-3">
                      <svg
                        className="animate-spin w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span className="text-sm">Uploading files...</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={handleCancelCreate}
                  disabled={isCreatingStory}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateStory}
                  disabled={!newStory.title.trim() || isCreatingStory}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {isCreatingStory && (
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  <span>
                    {isCreatingStory ? "Creating..." : "Create Story"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Backlog Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Backlog</h2>
            <p className="text-sm text-gray-600">
              {
                filteredBacklogItems.filter((item) => item.status === "backlog")
                  .length
              }{" "}
              of {getItemsForContainer("backlog").length} items shown
              {(searchTerm ||
                filterType !== "all" ||
                filterPriority !== "all" ||
                filterAssignee !== "all") &&
                " (filtered)"}
              • Drag items to sprints to plan your work
            </p>
          </div>

          <Droppable droppableId="backlog">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`min-h-[100px] transition-colors ${
                  snapshot.isDraggingOver ? "bg-blue-50" : ""
                }`}
              >
                {filteredBacklogItems
                  .filter((item) => item.status === "backlog")
                  .map((item, index) => renderBacklogItem(item, index))}
                {provided.placeholder}

                {filteredBacklogItems.filter(
                  (item) => item.status === "backlog"
                ).length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    {getItemsForContainer("backlog").length === 0 ? (
                      <>
                        <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                        <p>No items in backlog</p>
                      </>
                    ) : (
                      <>
                        <Search className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                        <p>No items match your current filters</p>
                        <p className="text-sm">
                          Try adjusting your search or filter criteria
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </div>

        {/* Future Sprints */}
        {futureSprints.map((sprint) => {
          const sprintItems = getItemsForContainer(sprint.id);
          const isCollapsed = collapsedSprints.has(sprint.id);
          const summary = getSprintSummary(sprint.id);

          return (
            <div key={sprint.id} className="bg-white rounded-lg shadow">
              <div
                className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleSprintCollapse(sprint.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {isCollapsed ? (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {sprint.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {sprint.description}
                      </p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(sprint.startDate)} -{" "}
                          {formatDate(sprint.endDate)}
                        </span>
                        <span className="flex items-center">
                          <Target className="w-3 h-3 mr-1" />
                          {summary.totalStoryPoints} points
                        </span>
                        <span className="flex items-center">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          {summary.totalTasks} tasks
                        </span>
                        <span className="flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          {summary.assignedTasks}/{summary.totalTasks} assigned
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        sprint.status === "active"
                          ? "bg-green-100 text-green-800"
                          : sprint.status === "planning"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {sprint.status}
                    </span>
                    <span className="text-sm text-gray-500">
                      {sprintItems.length} items
                    </span>
                  </div>
                </div>
              </div>

              {!isCollapsed && (
                <Droppable droppableId={String(sprint.id)}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`min-h-[60px] transition-colors ${
                        snapshot.isDraggingOver ? "bg-green-50" : ""
                      }`}
                    >
                      {sprintItems.map((item, index) =>
                        renderBacklogItem(item, index)
                      )}
                      {provided.placeholder}

                      {sprintItems.length === 0 && (
                        <div className="p-6 text-center text-gray-400">
                          <p>Drop items here to add them to this sprint</p>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              )}
            </div>
          );
        })}

        {futureSprints.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-2" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No Future Sprints
            </h3>
            <p className="text-gray-500">
              Create sprints in the admin panel to start planning your work
            </p>
          </div>
        )}
      </div>

      {isModalOpen && selectedTask && (
        <StoryModal
          task={selectedTask}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          sprints={sprints}
          onUpdate={(updatedTask) => {
            // Update the local backlog items
            setBacklogItems((prevItems) =>
              prevItems.map((item) =>
                item.id === updatedTask.id ? updatedTask : item
              )
            );
            // Call parent update if available
            if (onUpdateTask) {
              onUpdateTask(updatedTask);
            }
            // Update the selected task in the modal
            setSelectedTask(updatedTask);
          }}
        />
      )}
    </DragDropContext>
  );
};

export default BacklogView;
